import { useEffect, useState } from "react";

import { CheckCircle, FileText, Loader, Mail, RefreshCw, ShieldAlert, X } from "lucide-react";

import { requestPendingAuditReport } from "../api/audit";
import { useAuth } from "../contexts/AuthContext";

/**
 * Shown when an audit fails because the site can't be crawled; offers fix steps and an emailed
 * report once a background retry succeeds.
 *
 * @param {object} props
 * @param {() => void} props.onClose - Dismiss the dialog
 * @param {() => void} props.onRetry - Run the audit again
 * @param {string} props.url - The website whose audit failed
 */
const AuditErrorDialog = ({ onClose, onRetry, url }) => {
  const { user } = useAuth();
  // Audits require login, so prefill with the account email (still editable).
  const [email, setEmail] = useState(user?.email ?? "");
  const [emailStatus, setEmailStatus] = useState(null); // null | 'submitting' | 'success' | 'error'
  const [emailError, setEmailError] = useState("");
  const [activeTab, setActiveTab] = useState("why"); // 'why' | 'fix'

  // Lock body scroll while dialog is mounted
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setEmailStatus("submitting");
    setEmailError("");

    try {
      await requestPendingAuditReport(url, email.trim());
      setEmailStatus("success");
    } catch (err) {
      // 422 validation errors carry an array `detail`, which can't be rendered as text.
      const detail = err.response?.data?.detail;
      setEmailError(
        typeof detail === "string"
          ? detail
          : "Failed to submit. Please check the email and try again.",
      );
      setEmailStatus("error");
    }
  };

  return (
    <div className="audit-loader-overlay">
      <div className="audit-loader-card audit-loader-card--compact border-error/30">
        {/* Sized to fit a laptop screen; on shorter viewports the body scrolls and the actions stay pinned. */}
        <div className="w-full min-h-0 overflow-y-auto flex flex-col items-center px-1">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-error-container flex items-center justify-center ring-2 ring-error/20 flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-error" />
            </div>
            <h2 className="text-2xl font-headline font-bold text-on-surface">
              Unable to Access Your Website
            </h2>
          </div>

          <p className="text-on-surface-variant text-lg mb-5 max-w-lg">
            It looks like your website may be blocking automated visitors, like our AI agent. This
            may not be ideal if you want to improve your LLM SEO
          </p>

          {/* Tab navigation */}
          <div className="flex w-full mb-4 border-b border-surface-container-high">
            <button
              type="button"
              onClick={() => setActiveTab("why")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                activeTab === "why"
                  ? "border-primary text-primary"
                  : "border-transparent text-on-surface-variant hover:text-on-surface"
              }`}
            >
              <ShieldAlert className="w-4 h-4" /> Why this matters
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("fix")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                activeTab === "fix"
                  ? "border-primary text-primary"
                  : "border-transparent text-on-surface-variant hover:text-on-surface"
              }`}
            >
              <FileText className="w-4 h-4" /> How to fix this
            </button>
          </div>

          <div className="w-full">
            {activeTab === "why" && (
              <>
                <div className="bg-error-container/30 border border-error/10 rounded-xl p-5 text-left w-full mb-4">
                  <h3 className="text-error font-semibold mb-2 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" /> Why this matters
                  </h3>
                  <p className="text-on-surface-variant text-sm leading-relaxed">
                    If our bots are blocked, it is highly likely that{" "}
                    <span className="text-on-surface font-medium">
                      ChatGPT, Gemini, Claude, and Perplexity
                    </span>{" "}
                    are also unable to read your content. This means your website is invisible to
                    the new generation of AI search.
                  </p>
                </div>

                {/* Email notification option */}
                <div className="bg-surface-container-low border border-surface-container-high rounded-xl p-5 text-left w-full">
                  <h3 className="text-primary font-semibold mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Get your report via email
                  </h3>
                  {emailStatus === "success" ? (
                    <div className="flex items-center gap-3 text-green-600">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      <p className="text-sm">
                        We will crawl your site in the background and email you the report within 24
                        hours.
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-on-surface-variant text-sm mb-4">
                        We will crawl your site in the background until we are able to generate your
                        report, and send it to your email.
                      </p>
                      <form onSubmit={handleEmailSubmit} className="flex gap-3">
                        <input
                          type="email"
                          required
                          aria-label="Email address for the report"
                          autoComplete="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="flex-1 bg-surface-container-highest border border-surface-container-high rounded-xl px-4 py-2.5 text-on-surface text-sm placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                        />
                        <button
                          type="submit"
                          disabled={emailStatus === "submitting"}
                          className="px-5 py-2.5 rounded-xl font-semibold text-sm text-on-primary bg-gradient-to-br from-primary to-primary-container hover:opacity-90 transition-colors shadow-lg shadow-primary/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {emailStatus === "submitting" ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" /> Sending...
                            </>
                          ) : (
                            <>
                              <Mail className="w-4 h-4" /> Email Me
                            </>
                          )}
                        </button>
                      </form>
                      {emailStatus === "error" && (
                        <p className="text-error text-xs mt-2">{emailError}</p>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {activeTab === "fix" && (
              <div className="bg-surface-container-low border border-surface-container-high rounded-xl p-5 text-left w-full">
                <h3 className="text-primary font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> How to fix this
                </h3>
                <p className="text-on-surface-variant text-sm mb-4">
                  You need to update your{" "}
                  <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded font-mono text-xs">
                    robots.txt
                  </code>{" "}
                  file to allow AI bots to crawl your site. Add the following lines:
                </p>

                <div className="bg-inverse-surface rounded-lg p-4 font-mono text-sm text-inverse-on-surface border border-surface-container-high relative group">
                  <div className="flex flex-col gap-1">
                    <span className="text-green-400">User-agent: *</span>
                    <span className="text-green-400">Allow: /</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <span className="text-gray-400 comment"># Or specifically for AI bots:</span>
                    <div className="flex flex-col gap-1 mt-1">
                      <span className="text-blue-400">User-agent: GPTBot</span>
                      <span className="text-blue-400">User-agent: ClaudeBot</span>
                      <span className="text-blue-400">User-agent: CCBot</span>
                      <span className="text-green-400">Allow: /</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4 w-full mt-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-6 rounded-xl font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" /> Close
          </button>
          <button
            onClick={onRetry}
            className="flex-1 py-3 px-6 rounded-xl font-bold text-on-primary bg-gradient-to-br from-primary to-primary-container hover:opacity-90 transition-colors shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditErrorDialog;
